/**
 * GradingCriteriaPage — /grading-criteria
 *
 * CRUD for baholash mezonlari (grading criteria).
 * Each criterion has a name and a list of score-band thresholds.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, X, BarChart2 } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'
import {
  listGradingCriteria, createGradingCriteria, updateGradingCriteria, deleteGradingCriteria,
} from '@/api/resources'
import {
  Button, PageHeader, EmptyState, Spinner, FIELD_CLS, SearchInput, SortTh, TableFooter,
} from '@/components/ui/primitives'
import { fetchAllPages } from '@/utils/fetchAll'
import usePaginationSort from '@/hooks/usePaginationSort'

// ── Default threshold template ─────────────────────────────────────────
const DEFAULT_THRESHOLDS = [
  { min_percent: 0,  max_percent: 54,  label: "Qoniqarsiz", grade_point: 2.0 },
  { min_percent: 55, max_percent: 69,  label: "Qoniqarli",  grade_point: 3.0 },
  { min_percent: 70, max_percent: 84,  label: "Yaxshi",     grade_point: 4.0 },
  { min_percent: 85, max_percent: 100, label: "A'lo",       grade_point: 5.0 },
]

// ── ThresholdEditor ────────────────────────────────────────────────────
function ThresholdEditor({ value, onChange }) {
  const { t } = useTranslation()
  const bands = value || []

  function updateBand(idx, field, val) {
    const next = bands.map((b, i) => i === idx ? { ...b, [field]: val } : b)
    onChange(next)
  }

  function addBand() {
    onChange([...bands, { min_percent: 0, max_percent: 100, label: '', grade_point: '' }])
  }

  function removeBand(idx) {
    onChange(bands.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_1fr_2fr_1fr_auto] gap-2 text-xs font-medium text-white/45 px-1">
        <span>{t('grading.minPercent', 'Min %')}</span>
        <span>{t('grading.maxPercent', 'Max %')}</span>
        <span>{t('grading.label', 'Baho nomi')}</span>
        <span>{t('grading.gradePoint', 'Ball')}</span>
        <span></span>
      </div>
      {bands.map((band, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_1fr_2fr_1fr_auto] gap-2 items-center">
          <input
            type="number" min="0" max="100"
            value={band.min_percent}
            onChange={e => updateBand(idx, 'min_percent', Number(e.target.value))}
            className={FIELD_CLS}
            placeholder="0"
          />
          <input
            type="number" min="0" max="100"
            value={band.max_percent}
            onChange={e => updateBand(idx, 'max_percent', Number(e.target.value))}
            className={FIELD_CLS}
            placeholder="100"
          />
          <input
            type="text"
            value={band.label}
            onChange={e => updateBand(idx, 'label', e.target.value)}
            className={FIELD_CLS}
            placeholder={t('grading.labelPlaceholder', "Masalan: A'lo")}
          />
          <input
            type="number" min="0" step="0.1"
            value={band.grade_point}
            onChange={e => updateBand(idx, 'grade_point', Number(e.target.value))}
            className={FIELD_CLS}
            placeholder="5.0"
          />
          <button
            type="button"
            onClick={() => removeBand(idx)}
            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
          >
            <X size={15} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addBand}
        className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
      >
        <Plus size={13} /> {t('grading.addBand', 'Chegara qo\'shish')}
      </button>
    </div>
  )
}

// ── SlideOver ──────────────────────────────────────────────────────────
function SlideOver({ open, title, onClose, children }) {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl border-l z-50 flex flex-col shadow-xl" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-base font-bold" style={{ color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors" style={{ color: 'var(--ink-3)' }} onMouseEnter={e => e.currentTarget.style.background='var(--bg-soft)'} onMouseLeave={e => e.currentTarget.style.background='transparent'}><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </>
  )
}

// ── CriteriaForm ───────────────────────────────────────────────────────
function CriteriaForm({ initial, onSaved, onCancel, t }) {
  const isEdit = !!initial
  const [name,        setName]        = useState(initial?.name || '')
  const [description, setDescription] = useState(initial?.description || '')
  const [thresholds,  setThresholds]  = useState(initial?.thresholds?.length ? initial.thresholds : DEFAULT_THRESHOLDS)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim()) { setError(t('common.required')); return }
    setSaving(true); setError(null)
    try {
      const payload = { name, description, thresholds }
      if (isEdit) await updateGradingCriteria(initial.id, payload)
      else await createGradingCriteria(payload)
      onSaved()
    } catch (err) {
      setError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-1">
        <label className="block text-sm font-medium text-white/72">{t('grading.name', 'Nomi')} *</label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          className={FIELD_CLS}
          placeholder={t('grading.namePlaceholder', "Masalan: 5'li tizim")}
        />
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-white/72">{t('grading.description', 'Izoh')}</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          className={FIELD_CLS}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-white/72">{t('grading.thresholds', 'Baholash chegaralari')}</label>
        <p className="text-xs text-white/45">
          {t('grading.thresholdsHint', 'Foiz oralig\'i va unga mos baho nomini kiriting. Foizlar bir-biriga mos kelmasligi kerak.')}
        </p>
        <ThresholdEditor value={thresholds} onChange={setThresholds} />
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2">{error}</p>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
        <Button variant="secondary" type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={saving}>{t('common.save')}</Button>
      </div>
    </form>
  )
}

// ── GradingCriteriaPreview ─────────────────────────────────────────────
function GradingCriteriaPreview({ criteria, t, onClose }) {
  const sorted = [...(criteria.thresholds || [])].sort((a, b) => b.min_percent - a.min_percent)
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-white/45 uppercase mb-1">{t('grading.name', 'Nomi')}</p>
        <p className="text-sm font-semibold text-white">{criteria.name}</p>
      </div>
      {criteria.description && (
        <div>
          <p className="text-xs font-medium text-white/45 uppercase mb-1">{t('grading.description', 'Izoh')}</p>
          <p className="text-sm text-white/72">{criteria.description}</p>
        </div>
      )}
      <div>
        <p className="text-xs font-medium text-white/45 uppercase mb-2">{t('grading.thresholds', 'Chegaralar')}</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-white/45 border-b border-white/10">
              <th className="text-left py-2">{t('grading.label', 'Baho')}</th>
              <th className="text-center py-2">{t('grading.gradePoint', 'Ball')}</th>
              <th className="text-right py-2">{t('grading.range', 'Foiz oralig\'i')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((band, idx) => (
              <tr key={idx} className="border-b border-gray-50 dark:border-slate-800">
                <td className="py-2 font-medium text-white">{band.label}</td>
                <td className="py-2 text-center text-blue-600 font-semibold">{band.grade_point}</td>
                <td className="py-2 text-right text-white/45">{band.min_percent}% – {band.max_percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end pt-3 border-t border-white/10">
        <Button variant="secondary" onClick={onClose}>{t('common.close', 'Yopish')}</Button>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────
export default function GradingCriteriaPage() {
  const { t } = useTranslation()
  const [items,   setItems]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [panel,   setPanel]   = useState(null) // { mode: 'create'|'edit'|'view', item? }
  const [search,  setSearch]  = useState('')
  const [confirm, setConfirm] = useState(null)

  function fetchAll() {
    setLoading(true)
    fetchAllPages(listGradingCriteria)
      .then(setItems)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }

  const fetchedRef = useRef(false)
  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    fetchAll()
  }, [])

  async function handleConfirmDelete() {
    await deleteGradingCriteria(confirm)
    setConfirm(null)
    fetchAll()
  }

  const filtered = items.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()))
  const { paginated, page, setPage, pageSize, setPageSize, sortKey, sortDir, handleSort, totalPages, totalCount } = usePaginationSort(filtered)

  const panelTitle =
    panel?.mode === 'create' ? t('grading.createNew', 'Yangi mezon') :
    panel?.mode === 'edit'   ? t('common.edit') :
    panel?.mode === 'view'   ? t('common.view', 'Ko\'rish') : ''

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('nav.gradingCriteria', 'Baholash mezonlari')}
        subtitle={`${filtered.length} ${t('common.total').toLowerCase()}`}
        actions={
          <Button icon={<Plus size={15} />} onClick={() => setPanel({ mode: 'create' })}>
            {t('grading.createNew', 'Yangi mezon')}
          </Button>
        }
      />

      <SearchInput
        value={search}
        onChange={e => { setSearch(e.target.value); setPage(1) }}
        placeholder={t('common.search', 'Qidirish...')}
      />

      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : error ? (
          <div className="p-6 text-red-600 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<BarChart2 size={48} />}
            title={t('grading.empty', 'Baholash mezonlari yo\'q')}
            action={
              <Button icon={<Plus size={15} />} onClick={() => setPanel({ mode: 'create' })}>
                {t('grading.createNew', 'Yangi mezon')}
              </Button>
            }
          />
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <SortTh sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="pl-4 pr-2">{t('grading.name', 'Nomi')}</SortTh>
                  <th className="tc">{t('grading.bandsCount', 'Chegaralar')}</th>
                  <th>{t('grading.preview', 'Ko\'rinish')}</th>
                  <th>{t('common.actions', 'Amallar')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(item => {
                  const sorted = [...(item.thresholds || [])].sort((a, b) => b.min_percent - a.min_percent)
                  return (
                    <tr key={item.id}>
                      <td>
                        <p className="primary">{item.name}</p>
                        {item.description && (
                          <p className="secondary truncate max-w-xs">{item.description}</p>
                        )}
                      </td>
                      <td className="tc"><span className="secondary">{item.thresholds?.length ?? 0}</span></td>
                      <td>
                        <div className="flex items-center gap-1 flex-wrap">
                          {sorted.slice(0, 4).map((band, idx) => (
                            <span key={idx} className="text-xs px-1.5 py-0.5 rounded font-medium"
                              style={{ background: 'var(--lav-tint)', color: 'var(--lav)' }}>
                              {band.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="tr">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setPanel({ mode: 'view', item })}
                            className="tbl-btn tbl-go"
                            title={t('common.view', 'Ko\'rish')}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          <button
                            onClick={() => setPanel({ mode: 'edit', item })}
                            className="tbl-btn tbl-edit"
                            title={t('common.edit')}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => setConfirm(item.id)}
                            className="tbl-btn tbl-danger"
                            title={t('common.delete')}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <TableFooter page={page} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </>
        )}
      </div>

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      <SlideOver open={!!panel} title={panelTitle} onClose={() => setPanel(null)}>
        {panel?.mode === 'view' && panel.item && (
          <GradingCriteriaPreview criteria={panel.item} t={t} onClose={() => setPanel(null)} />
        )}
        {(panel?.mode === 'create' || panel?.mode === 'edit') && (
          <CriteriaForm
            key={panel?.item?.id ?? 'new'}
            initial={panel?.mode === 'edit' ? panel.item : null}
            t={t}
            onSaved={() => { setPanel(null); fetchAll() }}
            onCancel={() => setPanel(null)}
          />
        )}
      </SlideOver>
    </div>
  )
}

